import { useEffect, useLayoutEffect, useState } from 'react';
import { AuthProvider, useAuth } from 'auth-sso-kit/client/index.js';

import LoginPage from './pages/LoginPage.js';
import AppShell from './components/AppShell.js';
import { ThemeProvider } from './components/ThemeContext.js';
import { ToastProvider } from './components/Toast.js';
import { LocaleProvider } from './i18n/index.js';
import { DEFAULT_PAGE, findPage, type PageId } from './pages/registry.js';
import { parseRoute as parseRouteBody, RouteParamsContext, type Route } from './routes.js';

/**
 * The resolved route. Pages are static ids from the registry, with two parameterised detail
 * routes on top: `#person?id=7` and `#org?id=3`. Those resolve to their own hidden page ids;
 * the registry's `navOwner` is what keeps People / Organisations lit while one is open.
 *
 * `key` is what the scroll reset watches: it differs between '#people' and '#person?id=7', and
 * between two different people, so each navigation starts at the top.
 *
 * The parser itself lives in routes.ts — with the destination table and useDeepLink — so it can
 * be tested without booting the page registry. The one thing it cannot know is which ids are
 * real pages; that stays here, with the registry.
 */
const resolvePage = (head: string): PageId => (findPage(head)?.id ?? DEFAULT_PAGE) as PageId;

function parseRoute(): Route {
  return parseRouteBody(window.location.hash.slice(1) || DEFAULT_PAGE, resolvePage);
}

function AppContent() {
  const { user, loading } = useAuth();
  const [route, setRoute] = useState<Route>(parseRoute);

  useEffect(() => {
    const onHash = () => {
      const next = parseRoute();
      const def = findPage(next.page)!;
      if (def.adminOnly && user && !user.isAdmin) {
        window.location.hash = DEFAULT_PAGE;
        return;
      }
      setRoute(next);
    };
    window.addEventListener('hashchange', onHash);
    onHash();
    return () => window.removeEventListener('hashchange', onHash);
  }, [user]);

  /**
   * A new page starts at the top. Hash routing swaps the page component but leaves the
   * document's scroll offset alone, so without this any page inherits whatever offset the
   * previous one had.
   *
   * Keyed on `route.key` and nothing else: it fires exactly once per route change — including
   * people → person and person → person(other id), which are different keys — and never on a
   * re-render, so it cannot fight an in-page scroll. useLayoutEffect so the reset lands before
   * paint and there is no visible jump.
   */
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [route.key]);

  if (loading) {
    return <div className="login-page"><div className="spinner spinner-lg" /></div>;
  }

  if (!user) {
    return <LoginPage />;
  }

  const def = findPage(route.page)!;
  const PageComponent = def.component;

  return (
    <ThemeProvider>
      <ToastProvider>
        {/* The query string is published to the mounted page as one-shot arrival intents. The
            page reads them with useDeepLink and they are cleared on arrival — which is why the
            PageComponent below is NOT keyed on them. */}
        <RouteParamsContext.Provider value={route.params}>
          <AppShell currentPage={route.page}>
            {/* A detail page IS keyed on its subject: #person?id=7 → #person?id=3 is a
                different person, not a filter, so it remounts with fresh state. */}
            <PageComponent
              key={route.key}
              personId={route.personId ?? undefined}
              organisationId={route.organisationId ?? undefined}
              runId={route.runId ?? undefined}
            />
          </AppShell>
        </RouteParamsContext.Provider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <LocaleProvider>
        <AppContent />
      </LocaleProvider>
    </AuthProvider>
  );
}
